import React from 'react';
import { StageSection, Prose, KeyPoints, FactGrid } from '../../ContentPrimitives';
import { StageBlock, content, question } from '../../types';

export const meta = {
    title: 'Mining & Proof of Work',
    tagline: 'A hash puzzle that keeps blocks arriving every ten minutes.',
};

// This stage expects 3 quiz questions configured in the admin panel, in this order:
//   Q0 - the purpose of the difficulty adjustment
//   Q1 - what a 51% attack actually requires
//   Q2 - where new bitcoins come from
export const blocks: StageBlock[] = [
    content('the-puzzle', () => (
        <StageSection gradientBorder eyebrow="Stage 7 of 8" title="Mining is searching for a rare hash">
            <Prose>
                To propose the next block, a miner has to find a header whose hash falls below a target value. There
                is no shortcut: miners repeatedly tweak a nonce field and rehash the header, over and over, until
                one of them gets lucky. Whoever finds a qualifying hash first gets to add their block to the chain
                and collects the block reward plus the fees from every transaction in it.
            </Prose>
            <FactGrid items={[
                ['Block time', 'New blocks arrive roughly every 10 minutes, on average.'],
                ['Difficulty target', 'Adjusted periodically so block time stays roughly constant.'],
            ]} />
        </StageSection>
    )),

    content('difficulty-adjustment', () => (
        <StageSection title="Difficulty adjusts so block time doesn't">
            <Prose>
                Roughly every two weeks, the network automatically recalculates how hard the hash puzzle needs to be,
                based on how quickly recent blocks were actually found. If total mining power (hashrate) has grown,
                the puzzle gets harder; if it's dropped, the puzzle gets easier. The goal is keeping the average time
                between blocks close to 10 minutes, regardless of how much computing power is competing.
            </Prose>
        </StageSection>
    )),

    question(0),

    content('why-secure', () => (
        <StageSection title="Why proof of work secures the chain">
            <Prose>
                Finding a valid hash requires real, unavoidable computational work; there's no way to fake having
                done it cheaply. Rewriting several blocks of history would mean re-mining all of them faster than
                the honest network extends the real chain, which requires controlling more than half of all
                network hashpower sustained over that period, a "51% attack." This is Bitcoin's answer to the same
                problem Ethereum's proof-of-stake solves differently: instead of putting economic stake at risk for
                misbehavior, proof of work makes attacking the chain require an enormous, ongoing expenditure of
                real-world computation and electricity.
            </Prose>
        </StageSection>
    )),

    question(1),

    content('new-supply', () => (
        <StageSection title="Where new bitcoin comes from">
            <Prose>
                Mining is also how new bitcoin enters circulation: each mined block currently pays its miner a fixed
                "block subsidy" on top of the fees, and that subsidy is cut in half at regular, predetermined
                intervals. Total issuance approaches a hard cap of 21 million bitcoin, entirely by protocol rule,
                not by decision.
            </Prose>
            <KeyPoints items={[
                <>No one decides to "print" more bitcoin; issuance follows a fixed, publicly known schedule.</>,
                <>One stage left: how thousands of independent nodes actually agree on all of this without a coordinator.</>,
            ]} />
        </StageSection>
    )),

    question(2),
];
